export function createDefaultSchedulePreferences() {
    return {
        hard_tasks_hours: ['08:00-12:00'],
        easy_tasks_hours: ['17:00-22:00'],
        break_preferred_hours: ['14:00-16:00'],
        min_task_duration: 15,
        buffer_between_tasks: 10,
        max_deep_work_stretch: 120,
        batch_similar_tasks: true,
    };
}
export function createDefaultScheduleConstraints() {
    return {
        work_start: '08:00',
        work_end: '22:00',
        blocked_slots: [],
        preferences: createDefaultSchedulePreferences(),
    };
}
export function createDefaultScheduleSummary() {
    return {
        total_scheduled_minutes: 0,
        total_completed_minutes: 0,
        completion_rate: 0,
        tasks_completed: 0,
        tasks_remaining: 0,
        tasks_behind_schedule: 0,
    };
}
/**
 * Day-plan statistics derived from ground truth, not stored counters.
 *
 * The stored ScheduleSummary used to be mutated incrementally on each
 * completion, rebuilding its denominator as completed + remaining. Completing
 * a task that was NOT on the plan inflated the numerator while the
 * denominator trailed one behind — 7 completions over a 6-item history read
 * 117%, and finishing more made the number FALL toward 100 (10/9 = 111%).
 *
 * Semantics here, by design:
 * - plan   = unique non-buffer scheduled items; the denominator is the plan
 *            size and nothing else, so Progress is monotonic and ≤ 100
 * - done   = everything recorded completed for the day, INCLUDING off-plan
 *            work (a project task finished today still counts as work done)
 * - remaining = plan items not yet done
 * - progress  = (planDone + meetingsDone) / (planSize + meetings), capped;
 *               with nothing planned at all, any completion counts as 100
 *               (you did things; nothing was owed)
 *
 * **Meetings count.** A day with five meetings and no tasks used to read 0%,
 * which is simply false — those hours are the day. Meetings from a connected
 * calendar join the plan, and one counts as done once it has ended; there is
 * nothing to tick, and inventing a tick for somebody else's event would be
 * worse than reading the clock. They deliberately do NOT touch `completed`,
 * `offPlanDone` or `remaining`, which stay task-only so every existing tile
 * keeps meaning what it says — only the percentage widens.
 *
 * Progress stays monotonic: time only moves forward, so `meetingsDone` only
 * grows, and the denominator is fixed for the day.
 */
/**
 * Was this planned item done on this plan's day?
 *
 * The per-row answer `computeScheduleStats` adds up: done means the day's plan
 * recorded the completion. Week, Month and the day views also accepted
 * `task.status === 'completed'`, so a task planned Monday, missed, and
 * finished Wednesday ticked Monday too — the card said "1/1 done" while the
 * day's own header (from computeScheduleStats) said 0.
 */
export function slotDone(schedule, taskId) {
    return (schedule?.completed_tasks ?? []).includes(taskId);
}
/**
 * A day's planned rows counted the way computeScheduleStats counts them:
 * once per task, however many slots it was split into.
 */
export function planCounts(rows) {
    const all = new Set();
    const done = new Set();
    for (const row of rows) {
        all.add(row.id);
        if (row.is_completed)
            done.add(row.id);
    }
    return { total: all.size, completed: done.size };
}
export function computeScheduleStats(schedule, options) {
    const planIds = new Set((schedule.scheduled_tasks ?? [])
        .filter(st => !st.is_buffer)
        .map(st => st.task_id));
    const done = new Set(schedule.completed_tasks ?? []);
    let planDone = 0;
    planIds.forEach(id => { if (done.has(id))
        planDone++; });
    const completed = done.size;
    const offPlanDone = completed - planDone;
    const remaining = Math.max(0, planIds.size - planDone);
    const now = options?.now ?? new Date();
    const timed = (options?.meetings ?? []).filter((m) => !m.all_day);
    const meetingsPlanned = timed.length;
    const meetingsDone = timed.filter((m) => {
        const end = new Date(m.end).getTime();
        return Number.isFinite(end) && end <= now.getTime();
    }).length;
    const denominator = planIds.size + meetingsPlanned;
    const progress = denominator > 0
        ? Math.min(100, ((planDone + meetingsDone) / denominator) * 100)
        : (completed > 0 ? 100 : 0);
    return {
        planSize: planIds.size,
        planDone,
        completed,
        offPlanDone,
        remaining,
        progress,
        meetingsPlanned,
        meetingsDone,
    };
}
/**
 * How much of today's working window is still ahead, in minutes.
 *
 * Overnight-aware, because plenty of people work 15:00 to 05:00 and that
 * shape has two windows on the same calendar day: the small hours, and the
 * evening. Used to explain an empty plan rather than let the button look
 * broken — the scheduler correctly refuses to book work into hours you've
 * said you don't work, but it did so silently.
 */
export function workWindowRemaining(workStart, workEnd, now) {
    const toMinutes = (time) => {
        const [h, m] = time.split(':').map(Number);
        return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
    };
    const start = toMinutes(workStart);
    const end = toMinutes(workEnd);
    const current = now.getHours() * 60 + now.getMinutes();
    if (end > start) {
        // One window: [start, end].
        return Math.max(0, end - Math.max(current, start));
    }
    // Crosses midnight: [00:00, end] and [start, 24:00] on this same date.
    const small = current < end ? end - current : 0;
    const evening = 1440 - Math.max(current, start);
    return Math.max(0, small) + Math.max(0, evening);
}
/**
 * Why today's plan came out empty, in one sentence — or null when there is
 * no crisp reason and a generic empty state is the honest answer.
 */
export function explainEmptyPlan(context) {
    const { workStart, workEnd, isToday, eligibleTasks, now = new Date() } = context;
    if (eligibleTasks === 0) {
        return 'Nothing left to schedule — every task is finished, paused, or in a paused project.';
    }
    if (isToday && workWindowRemaining(workStart, workEnd, now) === 0) {
        return `Your working hours for today (${workStart}–${workEnd}) have already passed, ` +
            "so there's nowhere to put anything. Tomorrow's plan is unaffected.";
    }
    return null;
}
export function createSchedule(date) {
    return {
        date,
        user_id: 'default',
        constraints: createDefaultScheduleConstraints(),
        scheduled_tasks: [],
        completed_tasks: [],
        summary: createDefaultScheduleSummary(),
        generated_at: new Date().toISOString(),
        last_rescheduled_at: null,
    };
}
//# sourceMappingURL=schedule.js.map